// @givmo/app-admin — employee-invite decode, counts, and the announcement template (EP-WEB8).
//
// ── THE COUNTS PROBLEM, and how it was actually fixed ─────────────────────────────────────────────
//
// `GET /admin/company/invites` returns the standard paginated envelope PLUS a top-level `counts` key
// carrying per-status totals over ALL invites — computed server-side precisely so a summary row cannot
// understate the truth. The web client CANNOT SEE IT. The api-client response middleware is shape-gated
// on `{data} + {meta|links}` and rewrites the body to `body.data`, so `counts` (a sibling of `data`,
// not a member of it) is discarded before any caller runs, and there is no `meta.total` either.
//
// EP-BE12 closed that with the durable fix `agent_docs/api-client.md` prescribes: a DEDICATED endpoint
// returning a PLAIN object. `GET /admin/company/invites/counts` -> `EmployeeInviteCountsOut` has no
// `data` key and no envelope, so there is nothing for the unwrap to strip, and its numbers come from the
// same backend helper the list's unreachable sibling uses — the two can never disagree. Read it through
// {@link useInviteCounts}, never by adding rows up.
//
// The client-side derivation below ({@link fetchAllInvites} + {@link inviteCounts}) is NOT dead and must
// not be deleted. It has two live jobs:
//   1. THE ROWS. The tab renders the complete set and filters it in the client, so it still pages the
//      list to exhaustion via `collectAllPages` (the reports exporter's helper, which proves exhaustion
//      with a SHORT page and THROWS rather than silently truncating). That is a list need, not a counts
//      need, and it is why a huge invite history still surfaces an ERROR rather than a quiet half-count.
//   2. THE FALLBACK. `/invites/counts` does not exist on a backend that predates EP-BE12 — staging and
//      prod 404 it until the next promotion round. `useInviteCounts` therefore prefers the endpoint and
//      falls back to this derivation, so the surface is correct against both. Additive-tolerant in both
//      directions, exactly like the occasion-dates roster latch in `occasion-dates-decode.ts`.
// What is gone is deriving counts from the list as the PRIMARY path. Do not restore it, and do not
// "optimize" the derivation into a single first-page read either: a first page is not a count.
//
// ── WHAT AN INVITE IS ────────────────────────────────────────────────────────────────────────────
//
// A record that the company ASKED an email address to join — nothing more. It carries no user id and no
// roster PII, the invitee may have no Givmo account, and it grants nothing: enrollment still requires
// work-email OTP verification (or an operator-attested link). `ENROLLED` is stamped by a hook at the
// roster-link moment, not by the invite flow.
//
// `sent_at === null` means NO EMAIL HAS GONE OUT — the invite email is inert until its SendGrid template
// id is provisioned in the environment. The UI must say so plainly; "Invited" next to an email that was
// never delivered would have the admin waiting on a reply that can never come.

import {
  sdk,
  type EmployeeInviteCountsOut,
  type EmployeeInviteOut,
  type EmployeeInviteStatus,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { collectAllPages } from '../reports/csv-export';
import { PORTAL_WORDS, type EmployerModuleWords } from '../../lib/module-paths';

/**
 * The query key for "every invite for this company", shared by the tab's row list and by
 * `useInviteCounts`' fallback read so the two resolve to ONE cache entry and one fetch — a second key
 * over the same route would page the whole list twice whenever the counts endpoint is unavailable.
 */
export const INVITES_ALL_KEY = ['companies-list_company_employee_invites', 'all'] as const;

/** One page of invites, optionally filtered to a status. Tolerant decode via the canonical helper. */
async function fetchInvitesPage(
  status: EmployeeInviteStatus | undefined,
  offset: number,
  limit: number,
  signal?: AbortSignal,
): Promise<EmployeeInviteOut[]> {
  const { data } = await sdk.companiesListCompanyEmployeeInvites({
    query: {
      ...(status === undefined ? {} : { status }),
      'page[limit]': limit,
      'page[offset]': offset,
    },
    signal,
    throwOnError: true,
  });
  return extractList<EmployeeInviteOut>(data);
}

/**
 * Every invite for the company, newest-first with `INVITED` first (the server's ordering).
 *
 * Throws on a read failure and on an unbounded list (`collectAllPages`' page ceiling) — both are real
 * conditions the caller must surface, never swallow into a shorter list.
 */
export function fetchAllInvites(
  status?: EmployeeInviteStatus,
  signal?: AbortSignal,
): Promise<EmployeeInviteOut[]> {
  return collectAllPages((offset, limit) => fetchInvitesPage(status, offset, limit, signal));
}

export interface InviteCounts {
  invited: number;
  enrolled: number;
  revoked: number;
  total: number;
}

/** What the chips show before either counts source has answered. */
export const EMPTY_INVITE_COUNTS: InviteCounts = { invited: 0, enrolled: 0, revoked: 0, total: 0 };

/**
 * The dedicated counts endpoint's body, coerced.
 *
 * Every field is OPTIONAL on the wire — the backend model defaults each to 0, so none of them is in the
 * schema's `required` list — hence the defensive read rather than trusting the generated type's shape.
 * A non-finite or negative value is treated as absent instead of being rendered: a chip must never show
 * a number the data cannot back.
 *
 * `total` is the sum of the three, which is the whole of what this endpoint can express —
 * `EmployeeInviteCountsOut` has exactly three int fields and no room for a status added later. So if the
 * backend ever grows a fourth `EmployeeInviteStatus`, it must grow a fourth count field with it; the
 * next snapshot regen surfaces that as a new member here, and a chip has to be added to match. Until
 * then the sum IS the total. (The list-derived {@link inviteCounts} below counts an unclassifiable row
 * toward `total` precisely because it CAN see it — the two definitions agree on today's vocabulary.)
 */
export function toInviteCounts(wire: EmployeeInviteCountsOut | undefined | null): InviteCounts {
  const read = (value: number | undefined): number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
  const invited = read(wire?.invited);
  const enrolled = read(wire?.enrolled);
  const revoked = read(wire?.revoked);
  return { invited, enrolled, revoked, total: invited + enrolled + revoked };
}

/**
 * Per-status totals over a complete invite set — the FALLBACK derivation, for a backend that predates
 * `GET /admin/company/invites/counts`. Prefer {@link toInviteCounts} over the endpoint's own answer.
 *
 * An unrecognized status (a state added server-side after this build shipped) still counts toward
 * `total` but lands in no bucket — a forward-compatible reader must not drop a row it cannot classify,
 * and must not miscount it as one it recognizes.
 */
export function inviteCounts(invites: readonly EmployeeInviteOut[]): InviteCounts {
  let invited = 0;
  let enrolled = 0;
  let revoked = 0;
  for (const invite of invites) {
    if (invite.status === 'INVITED') invited += 1;
    else if (invite.status === 'ENROLLED') enrolled += 1;
    else if (invite.status === 'REVOKED') revoked += 1;
  }
  return { invited, enrolled, revoked, total: invites.length };
}

/** The status chips the toolbar offers, in lifecycle order. `null` = no filter (everything). */
export const INVITE_FILTERS: readonly { value: EmployeeInviteStatus | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'INVITED', label: 'Pending' },
  { value: 'ENROLLED', label: 'Enrolled' },
  { value: 'REVOKED', label: 'Revoked' },
];

/** How many rows match a chip, given the counts — so a chip never shows a number it cannot back. */
export function inviteFilterCount(
  counts: InviteCounts,
  value: EmployeeInviteStatus | null,
): number {
  if (value === null) return counts.total;
  if (value === 'INVITED') return counts.invited;
  if (value === 'ENROLLED') return counts.enrolled;
  return counts.revoked;
}

/** A human label for a status. Unknown values render verbatim rather than being coerced to a known one. */
export function inviteStatusLabel(status: EmployeeInviteStatus): string {
  if (status === 'INVITED') return 'Pending';
  if (status === 'ENROLLED') return 'Enrolled';
  if (status === 'REVOKED') return 'Revoked';
  return status;
}

/**
 * What to say about delivery. Three honest states, and none of them claims an email arrived:
 *   `sent`         — the server recorded a send at this timestamp
 *   `not-sent`     — an `INVITED` invite with no `sent_at`: nothing has gone out yet
 *   `not-appl`     — enrolled/revoked with no send on record; delivery is no longer the story
 */
export function inviteDeliveryNote(invite: EmployeeInviteOut): string {
  if (invite.sent_at != null && invite.sent_at.trim().length > 0) {
    return `Emailed ${formatInviteDate(invite.sent_at)}`;
  }
  if (invite.status === 'INVITED') return 'Not emailed yet';
  return '—';
}

/** True when a resend is even possible: only a pending invite can be re-sent (the server 400s others). */
export function canResendInvite(invite: EmployeeInviteOut): boolean {
  return invite.status === 'INVITED';
}

/** An ISO timestamp as a short "MMM D, YYYY". Falls back to the raw string rather than inventing a date. */
export function formatInviteDate(iso: string | null | undefined): string {
  if (iso == null || iso.trim().length === 0) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Split a pasted blob of addresses on commas, semicolons, and whitespace; de-duplicated, order kept. */
export function parseEmailInput(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of raw.split(/[\s,;]+/)) {
    const email = token.trim();
    if (email.length === 0) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

/**
 * The enrollment-rate view, or `null` when no rate can be computed.
 *
 * THE DENOMINATOR IS ADMIN-DECLARED. `company.number_of_employees` is the headcount the company typed
 * at registration and can edit in Settings — it is not a payroll feed, and nothing reconciles it. That
 * makes it the only denominator the data supports AND a number the admin can move, so the methodology
 * copy names it explicitly. A zero/absent declared headcount yields `null`, never a 0% or a >100%
 * artifact.
 */
export interface EnrollmentRateView {
  /** Fraction in [0, 1+]; not clamped, because "more enrolled than declared" is real and worth seeing. */
  ratio: number;
  active: number;
  declared: number;
}

export function toEnrollmentRate(
  active: number | null | undefined,
  declared: number | null | undefined,
): EnrollmentRateView | null {
  if (typeof active !== 'number' || !Number.isFinite(active) || active < 0) return null;
  if (typeof declared !== 'number' || !Number.isFinite(declared) || declared <= 0) return null;
  return { ratio: active / declared, active, declared };
}

/** The enrollment percentage, whole points. */
export function enrollmentPercentLabel(view: EnrollmentRateView): string {
  return `${Math.round(view.ratio * 100)}%`;
}

/**
 * The methodology sentence for the enrollment meter — it must name the declared denominator (§135).
 * Where that headcount is edited is the host's place (`words`, EP-MNT5(h)): the portal's Settings,
 * the console's Profile page; the portal's words are the default.
 */
export function enrollmentMethodology(
  view: EnrollmentRateView | null,
  words: EmployerModuleWords = PORTAL_WORDS,
): string {
  const basis = `The denominator is the headcount your company entered at sign-up${words.headcountEditable} — not a payroll feed, so it is only as current as you keep it.`;
  if (view === null) {
    return `Enrollment rate compares employees linked to your company against your declared headcount. ${words.headcountAddWhere} ${basis}`;
  }
  return `${view.active} of the ${view.declared} employees you told us you have are linked to your company program. ${basis}`;
}

/**
 * The pre-written announcement the admin sends THEMSELVES (§97). No backend, no invite email required,
 * and no signup link — because there is no company-scoped enrollment URL to give.
 *
 * Enrollment is DOMAIN-driven, not link-driven: an employee installs the app, adds their work email,
 * and verifies it by OTP; the domain is what resolves them to this company. So the honest template is
 * instructions plus the real store links — never a fabricated "join your company" URL, which is why
 * this surface deliberately ships no "copy signup link" control.
 */
export function announcementTemplate({
  companyName,
  domain,
  appStoreUrl,
  playStoreUrl,
}: {
  companyName: string;
  domain: string | null | undefined;
  appStoreUrl: string;
  playStoreUrl: string;
}): string {
  // With no domain on the wire we cannot name the address to verify, so the step says "work email"
  // generically rather than printing an "@undefined" the admin would have to catch.
  const workEmail =
    domain != null && domain.trim().length > 0 ? `@${domain.trim()}` : 'work email address';
  const emailStep =
    domain != null && domain.trim().length > 0
      ? `Add your ${workEmail} work email in the app and verify it with the code we send. That is what links your account to ${companyName}.`
      : `Add your ${workEmail} in the app and verify it with the code we send. That is what links your account to ${companyName}.`;

  return [
    `Subject: Donate to any charity — ${companyName} will match it`,
    '',
    'Hi team,',
    '',
    `We have partnered with Givmo so you can support any charity you care about and have ${companyName} match your donation.`,
    '',
    'Setting up takes about two minutes:',
    '',
    `1. Download the Givmo app — iPhone: ${appStoreUrl} · Android: ${playStoreUrl}`,
    '2. Create your Givmo account, or sign in if you already have one.',
    `3. ${emailStep}`,
    '4. Donate to a charity, then request a match from the app.',
    '',
    'Reply to this email if anything gets stuck and we will help.',
  ].join('\n');
}
